import { useSyncExternalStore } from "react";
import { db } from "./db";
import { API } from "./supabase";
import { hasWebGL2, ESRI_TILES } from "./maps";

/* Contractor offline: cached job records (7-day TTL) + queued complete/completion actions with backoff. */
const TTL = 7 * 86400000;
const PURGED = "[DATA EXPIRED & PURGED]";
const BACKOFF = [30000, 120000, 300000];
const STYLE = "https://tiles.openfreemap.org/styles/liberty";

let state = { pending: 0, flushing: false, lastResult: null };
const subs = new Set();
const set = (p) => { state = { ...state, ...p }; subs.forEach((f) => f()); };
export const useContractorQueue = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => state, () => state);

export const saveJob = async (token, job) => {
  if (!job || job.task_summary === PURGED) return;
  await db.put("contractor_jobs", { token, job, saved_at: Date.now() });
};
export const loadJob = async (token) => {
  const rec = await db.get("contractor_jobs", token);
  if (!rec) return null;
  if (Date.now() - rec.saved_at > TTL || (rec.job.expires_at && Date.now() - new Date(rec.job.expires_at) > TTL)) { await db.del("contractor_jobs", token); return null; }
  return rec.job;
};

/** Warm the SW caches: media (signed URLs are cached by path) + OpenFreeMap vector tiles/glyphs/sprites around the pin (≤ 60 tiles). */
const tileUrls = (tpl, lat0, lng0, maxz) => {
  const tiles = [];
  for (let z = 14; z <= 17; z++) {
    const zz = Math.min(z, maxz); // OpenFreeMap serves vector data to z14; z15–17 overzoom from z14, so only distinct tiles are fetched
    const n = 2 ** zz, lat = (lat0 * Math.PI) / 180;
    const cx = ((lng0 + 180) / 360) * n, cy = ((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * n;
    const r = Math.max(1, Math.ceil(500 / ((40075016 * Math.cos(lat)) / n)));
    for (let x = Math.floor(cx) - r; x <= Math.floor(cx) + r; x++) for (let y = Math.floor(cy) - r; y <= Math.floor(cy) + r; y++) { const u = tpl.replace("{z}", zz).replace("{x}", x).replace("{y}", y); if (!tiles.includes(u)) tiles.push(u); }
  }
  return tiles.slice(0, 60);
};
const vectorAssets = async (lat0, lng0) => {
  const style = await (await fetch(STYLE)).json();
  const src = Object.values(style.sources).find((x) => x.type === "vector");
  const tj = src.url ? await (await fetch(src.url)).json() : src;
  const tiles = tileUrls(tj.tiles[0], lat0, lng0, Math.min(tj.maxzoom ?? 14, 17));
  const fonts = new Set(); style.layers.forEach((l) => (l.layout?.["text-font"] || []).forEach((f) => fonts.add(f)));
  const glyphs = [...fonts].flatMap((f) => ["0-255", "256-511"].map((r) => style.glyphs.replace("{fontstack}", encodeURIComponent(f)).replace("{range}", r)));
  const sprite = typeof style.sprite === "string" ? [`${style.sprite}.json`, `${style.sprite}.png`, `${style.sprite}@2x.json`, `${style.sprite}@2x.png`] : [];
  return [...tiles, ...glyphs, ...sprite];
};

export const prefetchForOffline = async (job) => {
  const urls = [...(job.photo_signed_urls || []), ...(job.completion_photo_urls || []), job.audio_url].filter(Boolean);
  let tiles = [];
  if (job.latitude != null) {
    // No WebGL → the page renders Esri raster, so vector tiles would be dead weight; warm the raster cache instead (nothing without a key).
    if (hasWebGL2()) { try { tiles = await vectorAssets(job.latitude, job.longitude); } catch { /* map stays online-only */ } }
    else if (ESRI_TILES) tiles = tileUrls(ESRI_TILES, job.latitude, job.longitude, 17);
  }
  const all = [...urls, ...tiles];
  await Promise.allSettled(all.map((u) => fetch(u, { mode: /arcgis\.com|openfreemap\.org/.test(u) ? "cors" : "no-cors" })));
  return { media: urls.length, tiles: Math.min(tiles.length, 60) };
};

// ---- queue
export const queuedFor = async (token) => (await db.all("contractor_queue")).filter((q) => q.token === token).sort((a, b) => a.created_at - b.created_at);
const refresh = async () => set({ pending: (await db.all("contractor_queue")).length });

export const enqueueComplete = async (token, clientAt, secretHash) => {
  await db.put("contractor_queue", { id: `${token}:complete`, type: "complete", token, client_completed_at: clientAt, secret_hash: secretHash || null, created_at: Date.now(), attempts: 0 });
  await refresh(); registerSync(); return true;
};
export const enqueueCompletion = async (token, blobs, note, secretHash) => {
  await db.put("contractor_queue", { id: `${token}:completion`, type: "completion", token, blobs, note: note || null, secret_hash: secretHash || null, created_at: Date.now(), attempts: 0 });
  await refresh(); registerSync(); return true;
};

/** Overlay queued actions on a job record so the UI shows the local truth. */
export const applyQueue = (job, items) => {
  let j = job;
  for (const q of items) {
    if (q.type === "complete") j = { ...j, status: "completed", completed_at: q.client_completed_at, completed_at_client: q.client_completed_at, queued: true };
    if (q.type === "completion") j = { ...j, completion_photo_urls: [...(j.completion_photo_urls || []), ...q.blobs.map((b) => URL.createObjectURL(b))], completion_note: q.note || j.completion_note, completion_locked_at: new Date(q.created_at).toISOString(), queued: true };
  }
  return j;
};

const registerSync = async () => { try { const reg = await navigator.serviceWorker?.ready; await reg?.sync?.register("dropit-contractor"); } catch { /* unsupported */ } };

let timer = null, attempt = 0;
const schedule = () => { clearTimeout(timer); timer = setTimeout(flushQueue, BACKOFF[Math.min(attempt, BACKOFF.length - 1)]); attempt++; };

export const flushQueue = async (onUpdate) => {
  if (state.flushing) return;
  const items = (await db.all("contractor_queue")).sort((a, b) => a.created_at - b.created_at);
  await refresh();
  if (!items.length || !navigator.onLine) return;
  set({ flushing: true });
  let failed = false;
  for (const q of items) {
    try {
      let r;
      if (q.type === "complete") r = await fetch(`${API}/public/dispatch/${q.token}/complete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ client_completed_at: q.client_completed_at, secret_hash: q.secret_hash }) });
      else {
        const fd = new FormData(); q.blobs.forEach((b, i) => fd.append("photos", b, `done-${i}.jpg`)); if (q.note) fd.append("note", q.note); if (q.secret_hash) fd.append("secret_hash", q.secret_hash);
        r = await fetch(`${API}/public/dispatch/${q.token}/completion`, { method: "POST", body: fd });
      }
      if (r.ok || (r.status === 409 && q.type === "completion")) {
        const body = await r.json().catch(() => ({}));
        await db.del("contractor_queue", q.id);
        set({ lastResult: { id: q.id, type: q.type, status: r.status, body } });
        onUpdate?.(q, r.status, body);
      } else if (r.status >= 400 && r.status < 500) {  // terminal: drop the item but always tell the page why (never silent)
        const body = await r.json().catch(() => ({}));
        await db.del("contractor_queue", q.id);
        set({ lastResult: { id: q.id, type: q.type, status: r.status, body } });
        onUpdate?.(q, r.status, body);
      } else failed = true;
    } catch { failed = true; }
  }
  await refresh(); set({ flushing: false });
  if (failed) schedule(); else attempt = 0;
};

let wired = false;
export const wireQueue = (onUpdate) => {
  if (wired) return; wired = true;
  const go = () => flushQueue(onUpdate);
  window.addEventListener("online", go); document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && go());
  navigator.serviceWorker?.addEventListener("message", (e) => e.data?.type === "FLUSH_CONTRACTOR" && go());
  go();
};
